import { useEffect, useState } from "react";
import { format, parse, isValid, isBefore } from "date-fns";
import DatePicker from "react-datepicker";
import {
  chevronLeft,
  chevronRight,
} from "../../../utility-functions/assetLoader";
import "react-datepicker/dist/react-datepicker.css";
import "./DateFilterDropdown.css";

const today = new Date();

//Helper functions
const formatDateDisplay = (date) => {
  return date ? format(date, "do MMMM yyyy") : "";
};

const isValidDate = (date) => {
  if (date.length === 10) {
    const parsedDate = parse(date, "dd-MM-yyyy", new Date());
    return isValid(parsedDate) && !(parsedDate > today);
  }
  return false;
};

const DateFilter = ({
  initialStartDate = null,
  initialEndDate = null,
  onCancel = () => null,
  onApply = () => null,
  style = {},
}) => {
  const defaultStartDate = initialStartDate ?? today;
  const defaultEndDate = initialEndDate ?? today;

  //States
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [error, setError] = useState({
    startDate: "",
    endDate: "",
  });
  const [typingStartDate, setTypingStartDate] = useState(
    format(defaultStartDate, "dd-MM-yyyy")
  );
  const [typingEndDate, setTypingEndDate] = useState(
    format(defaultEndDate, "dd-MM-yyyy")
  );
  const [isTypingStart, setIsTypingStart] = useState(false);
  const [isTypingEnd, setIsTypingEnd] = useState(false);

  //Handlers
  const onChange = (dates) => {
    const [start, end] = dates;

    setStartDate(start);
    setEndDate(end);

    setTypingStartDate(start ? format(start, "dd-MM-yyyy") : "");
    setTypingEndDate(end ? format(end, "dd-MM-yyyy") : "");
  };

  const dateChangeHandler = (e, isStart) => {
    let input = e.target.value.replace(/\D/g, "");
    if (input.length > 8) input = input.slice(0, 8);

    const formattedInput = input.replace(
      /^(\d{2})(\d{0,2})?(\d{0,4})?$/,
      (_, d, m, y) => [d, m, y].filter(Boolean).join("-")
    );

    if (isStart) {
      setTypingStartDate(formattedInput);
    } else {
      setTypingEndDate(formattedInput);
    }
  };

  const handleDateBlur = (isStart) => {
    if (isStart) {
      setIsTypingStart(false);
      if (error.startDate === "") {
        setStartDate(parse(typingStartDate, "dd-MM-yyyy", new Date()));
      }
    } else {
      setIsTypingEnd(false);
      if (error.endDate === "") {
        setEndDate(parse(typingEndDate, "dd-MM-yyyy", new Date()));
      }
    }
  };

  const cancelHandler = () => {
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    setTypingStartDate(format(defaultStartDate, "dd-MM-yyyy"));
    setTypingEndDate(format(defaultEndDate, "dd-MM-yyyy"));
    onCancel?.(defaultStartDate, defaultEndDate);
  };

  const applyHandler = () => {
    if (!startDate || !endDate) {
      return;
    }
    onApply?.(startDate, endDate);
  };

  //Effects
  useEffect(() => {
    const startDateIsValid = isValidDate(typingStartDate);
    const endDateIsValid = isValidDate(typingEndDate);
    const parsedStartDate = parse(typingStartDate, "dd-MM-yyyy", new Date());
    const parsedEndDate = parse(typingEndDate, "dd-MM-yyyy", new Date());

    if (!startDateIsValid) {
      setError((prev) => ({ ...prev, startDate: "Invalid" }));
    } else {
      setError((prev) => ({ ...prev, startDate: "" }));
    }
    if (!endDateIsValid) {
      setError((prev) => ({ ...prev, endDate: "Invalid" }));
    } else if (
      startDateIsValid &&
      endDateIsValid &&
      isBefore(parsedEndDate, parsedStartDate)
    ) {
      setError((prev) => ({ ...prev, endDate: "Invalid" }));
    } else {
      setError((prev) => ({ ...prev, endDate: "" }));
    }
  }, [typingEndDate, typingStartDate]);

  return (
    <div style={style} className="date-filter-dropdown-container">
      <div className="date-filter-inputs-container">
        <div style={{ alignItems: error?.startDate ? "start" : "center" }}>
          <label>Start</label>
          <div>
            <input
              type="text"
              placeholder="dd-mm-yyyy"
              value={
                isTypingStart ? typingStartDate : formatDateDisplay(startDate)
              }
              onFocus={() => {
                setIsTypingStart(true);
              }}
              onBlur={() => handleDateBlur(true)}
              onChange={(e) => dateChangeHandler(e, true)}
            />
            {error?.startDate && (
              <p className="input-error">{error?.startDate}</p>
            )}
          </div>
        </div>

        <div
          style={{
            alignItems: error?.endDate ? "start" : "center",
            marginTop: "10px",
          }}
        >
          <label>End</label>
          <div>
            <input
              type="text"
              placeholder="dd-mm-yyyy"
              value={isTypingEnd ? typingEndDate : formatDateDisplay(endDate)}
              onFocus={() => {
                setIsTypingEnd(true);
              }}
              onBlur={() => handleDateBlur(false)}
              onChange={(e) => dateChangeHandler(e, false)}
            />
            {error?.endDate && <p className="input-error">{error?.endDate}</p>}
          </div>
        </div>
      </div>
      <DatePicker
        selected={startDate}
        onChange={onChange}
        startDate={startDate}
        endDate={endDate}
        selectsRange
        inline
        maxDate={new Date()}
        renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
          <div className="date-filter-dropdown-calendar-header">
            <button onClick={decreaseMonth}>
              {chevronLeft(20, 20, "#3F3F46")}
            </button>
            <span>
              {date.toLocaleString("default", {
                month: "long",
                year: "numeric",
              })}
            </span>
            <button onClick={increaseMonth}>
              {chevronRight("#3F3F46", 20, 20)}
            </button>
          </div>
        )}
      />
      <div className="action-buttons">
        <button className="cancel-btn" onClick={cancelHandler}>
          Cancel
        </button>
        <button
          onClick={applyHandler}
          disabled={!startDate || !endDate}
          className={`save-btn ${!startDate || !endDate ? "disabled" : ""}`}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default DateFilter;
